import type { CargoKind } from "@frontier/content/risk";
import {
  CARGO_STACK_LIMIT,
  MAX_CARGO_UNITS,
  MAX_CARGO_SLOTS,
  cargoGroups,
  cargoUsedSlots,
} from "@frontier/content/cargo-stacks";
import "./risk.css";
import type { ShipSnapshot } from "@frontier/protocol/contracts";
import { pressHandlers } from "./press-handlers";

export interface CargoHoldProps {
  pilot: Pick<
    ShipSnapshot,
    | "cargo"
    | "capacity"
    | "energyCargo"
    | "salvageCargo"
    | "contrabandCargo"
    | "pending"
    | "dead"
    | "docked"
  >;
  contested?: boolean;
  cargo?: readonly CargoKind[] | undefined;
  onDropContraband: () => void;
  onDropEnergy: () => void;
  onDropSalvage: () => void;
  onDropBattery?: () => void;
  onDropRocket?: () => void;
  onDropDrone?: () => void;
}

export function cargoView(
  pilot: CargoHoldProps["pilot"],
  cargo?: readonly CargoKind[],
) {
  const units = cargo?.length ?? Math.min(MAX_CARGO_UNITS, pilot.cargo);
  const awaiting = cargo === undefined &&
    pilot.energyCargo + pilot.salvageCargo + (pilot.contrabandCargo ?? 0) !== units;
  const contents = cargo ?? (awaiting ? [] : Array.from(
      { length: units },
      (_, index): CargoKind =>
        index < pilot.energyCargo
          ? "energy"
          : index < pilot.energyCargo + (pilot.contrabandCargo ?? 0)
            ? "contraband"
            : "salvage",
    ));
  const groups = cargoGroups(contents);
  const capacity = Math.min(MAX_CARGO_SLOTS, pilot.capacity);
  const used = cargoUsedSlots(contents);
  return { contents, groups, capacity, used, units, awaiting };
}

export function CargoHold({
  pilot,
  cargo,
  contested = false,
  onDropContraband,
  onDropEnergy,
  onDropSalvage,
  onDropBattery,
  onDropRocket,
  onDropDrone,
}: CargoHoldProps) {
  const { contents, groups, capacity, used, units, awaiting } = cargoView(pilot, cargo);
  const blocked = pilot.pending || pilot.dead || pilot.docked || awaiting;
  const labels: Record<CargoKind, string> = {
    energy: "Энергия",
    salvage: "Оборудование",
    contraband: "Контрабанда",
    battery: "Батареи",
    rocket: "Ракеты",
    drone: "Дроны",
  };
  const symbols: Record<CargoKind, string> = {
    energy: "◆",
    salvage: "▣",
    contraband: "⬡",
    battery: "ϟ",
    rocket: "➤",
    drone: "✚",
  };
  const actions: Record<
    CargoKind,
    { label: string; title: string; activate: (() => void) | undefined }
  > = {
    energy: {
      label: "Уничтожить 1 энергоконтейнер",
      title: "Уничтожить один энергоконтейнер из трюма",
      activate: onDropEnergy,
    },
    salvage: {
      label: "Уничтожить 1 контейнер оборудования",
      title: "Уничтожить один контейнер оборудования из трюма",
      activate: onDropSalvage,
    },
    contraband: {
      label: contested
        ? "Сбросить 1 контейнер контрабанды"
        : "Уничтожить 1 контейнер контрабанды",
      title: contested
        ? "Оставить один контейнер контрабанды в пространстве"
        : "Здесь отказ уничтожает один контейнер контрабанды",
      activate: onDropContraband,
    },
    rocket: { label: "Уничтожить 1 ракету", title: "Уничтожить одну ракету из трюма", activate: onDropRocket },
    drone: { label: "Уничтожить 1 дрон", title: "Уничтожить один дрон из трюма", activate: onDropDrone },
    battery: {
      label: "Уничтожить 1 батарею",
      title: "Уничтожить одну батарею из трюма",
      activate: onDropBattery,
    },
  };
  return (
    <div
      className="cargo-bar"
      aria-label="Грузовой отсек"
      data-cargo-units={units}
    >
      <div className="cargo-heading">
        <span className="cargo-label">ТРЮМ</span>
        <b
          aria-label={awaiting ? `Подтверждаем состав груза. Единиц: ${units}.` : `Занято ячеек: ${used} из ${capacity}. Единиц груза: ${units}.`}
          title={`По ${CARGO_STACK_LIMIT} контейнеров одного вида в ячейке`}
        >
          {awaiting ? "…" : used}/{capacity}
        </b>
        {contents.includes("contraband") && !contested && (
          <small className="cargo-abandon-hint">
            СБРОС ЗДЕСЬ УНИЧТОЖАЕТ ГРУЗ
          </small>
        )}
      </div>
      <p className="cargo-summary">
        {units} ед. груза · Свободно ячеек:{" "}
        {awaiting ? "…" : Math.max(0, capacity - used)}
      </p>
      {awaiting ? <p className="cargo-empty">Получаем состав груза…</p> : groups.length === 0 ? (
        <p className="cargo-empty">Трюм пуст</p>
      ) : (
        <div className="cargo-groups" role="list" aria-label="Виды груза">
          {groups.map((group) => {
            const action = actions[group.kind];
            return (
              <div
                key={group.kind}
                role="listitem"
                className={`cargo-group ${group.kind}-cargo`}
                data-cargo-kind={group.kind}
                data-cargo-count={group.count}
                data-cargo-slots={group.slots}
                aria-label={`${labels[group.kind]}: ${group.count} ед., ${group.slots} яч.`}
                title={
                  group.kind === "contraband"
                    ? "Контрабанда раскрывает район"
                    : undefined
                }
              >
                <span aria-hidden="true">{symbols[group.kind]}</span>
                <span className="cargo-group-description">
                  <b>{labels[group.kind]}</b>
                  <small>
                    {group.count} ед. · {group.slots} яч.
                  </small>
                </span>
                <button
                  type="button"
                  className={`drop-${group.kind}`}
                  title={action.title}
                  aria-label={action.label}
                  disabled={blocked || !action.activate}
                  {...pressHandlers(() => {
                    if (!blocked) action.activate?.();
                  })}
                >
                  <b aria-hidden="true">−1</b>
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
